import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Download, Trash2, Package, Loader2, FolderOpen,
  Image as ImageIcon, FileText, File as FileIcon, ExternalLink,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { eventsApi, documentsApi, downloadFile, fileUrl } from "@/lib/api";
import { formatShortDate } from "@/lib/helpers";
import { useCan } from "@/context/AuthContext";

function typeOf(ct, name = "") {
  if ((ct || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(name)) return "image";
  if ((ct || "").includes("pdf") || /\.pdf$/i.test(name)) return "pdf";
  return "doc";
}

export default function EventDocuments() {
  const canDelete = useCan("delete");
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [formFilter, setFormFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, d] = await Promise.all([eventsApi.get(id), documentsApi.forEvent(id)]);
      setEvent(ev);
      setDocs(d);
    } catch {
      toast.error("Failed to load documents");
      navigate(`/events/${id}/forms`);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const forms = useMemo(() => Array.from(new Set(docs.map((d) => d.form_name))), [docs]);

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      if (formFilter !== "all" && d.form_name !== formFilter) return false;
      if (typeFilter !== "all" && typeOf(d.content_type, d.filename) !== typeFilter) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        if (![d.guest_name, d.code, d.filename, d.field_label].some((v) => (v || "").toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [docs, formFilter, typeFilter, q]);

  const toggle = (url) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(url) ? n.delete(url) : n.add(url);
      return n;
    });
  };
  const allChecked = filtered.length > 0 && filtered.every((d) => selected.has(d.url));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(filtered.map((d) => d.url)));
  };

  const zip = async (list) => {
    if (!list.length) { toast.message("No documents selected"); return; }
    const files = list.map((d) => ({ url: d.url, filename: d.filename, folder: `${d.code} ${d.guest_name || ""}`.trim() }));
    try {
      await downloadFile(`/documents/download-zip`, `${event?.slug || "event"}-documents.zip`, { method: "post", data: { files } });
    } catch { toast.error("Download failed"); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"? This removes it from the submission.`)) return;
    await documentsApi.delete({ url: d.url, submission_id: d.submission_id });
    toast.success("Document deleted");
    setSelected((prev) => { const n = new Set(prev); n.delete(d.url); return n; });
    load();
  };

  const selectedDocs = filtered.filter((d) => selected.has(d.url));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <button onClick={() => navigate(`/events/${id}/forms`)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-forms">
          <ArrowLeft className="h-4 w-4" /> Back to forms
        </button>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow text-gold">Documents</p>
            <h1 className="mt-1 font-serif text-3xl font-semibold">{event?.title || "Event"}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{docs.length} file{docs.length === 1 ? "" : "s"} collected across all forms</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => zip(selectedDocs)} disabled={!selected.size} data-testid="download-selected" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm font-semibold disabled:opacity-50"><Package className="h-4 w-4" /> Download Selected ({selected.size})</button>
            <button onClick={() => zip(filtered)} disabled={!filtered.length} data-testid="download-all" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"><Download className="h-4 w-4" /> Download All</button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guest, ID or file name" data-testid="docs-search" className="w-full rounded-xl border border-input bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/20" />
          </div>
          <select value={formFilter} onChange={(e) => setFormFilter(e.target.value)} data-testid="docs-form-filter" className="rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:border-gold">
            <option value="all">All forms</option>
            {forms.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="docs-type-filter" className="rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:border-gold">
            <option value="all">All types</option>
            <option value="image">Images</option>
            <option value="pdf">PDFs</option>
            <option value="doc">Documents</option>
          </select>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
          {loading ? (
            <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
          ) : filtered.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-center text-sm text-muted-foreground">
              <FolderOpen className="mb-2 h-8 w-8" /> No documents found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="docs-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3"><input type="checkbox" checked={allChecked} onChange={toggleAll} className="accent-[hsl(var(--gold))]" data-testid="docs-select-all" /></th>
                    <th className="px-4 py-3">File</th>
                    <th className="px-4 py-3">Guest</th>
                    <th className="px-4 py-3">Form / Field</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d, i) => {
                    const t = typeOf(d.content_type, d.filename);
                    const Icon = t === "image" ? ImageIcon : t === "pdf" ? FileText : FileIcon;
                    return (
                      <tr key={`${d.url}-${i}`} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid={`doc-row-${i}`}>
                        <td className="px-4 py-3"><input type="checkbox" checked={selected.has(d.url)} onChange={() => toggle(d.url)} className="accent-[hsl(var(--gold))]" data-testid={`doc-check-${i}`} /></td>
                        <td className="px-4 py-3">
                          <a href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="inline-flex max-w-[220px] items-center gap-2 font-medium hover:text-gold">
                            <Icon className="h-4 w-4 shrink-0 text-gold" /><span className="truncate">{d.filename || "File"}</span>
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{d.guest_name || "—"}</div>
                          <div className="font-mono text-xs text-muted-foreground">{d.code}</div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground"><div>{d.form_name}</div><div className="text-xs">{d.field_label}</div></td>
                        <td className="px-4 py-3 text-muted-foreground">{formatShortDate((d.created_at || "").slice(0, 10))}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <a href={fileUrl(d.url)} target="_blank" rel="noreferrer" title="View" className="rounded-lg border border-border p-2 hover:bg-secondary"><ExternalLink className="h-4 w-4" /></a>
                            <a href={fileUrl(d.url)} download target="_blank" rel="noreferrer" title="Download" className="rounded-lg border border-border p-2 hover:bg-secondary"><Download className="h-4 w-4" /></a>
                            {canDelete && (<button onClick={() => del(d)} data-testid={`doc-del-${i}`} title="Delete" className="rounded-lg border border-border p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
